import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FormTemplate } from '../../types/formBuilder';
import { FormSubmission, OptimizedFormSubmissionService } from '../../services/optimizedFormSubmissionService';
import { TemplateService } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ExpertModePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExpertModePanel: React.FC<ExpertModePanelProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [brokenSubmissions, setBrokenSubmissions] = useState<FormSubmission[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<FormTemplate[]>([]);
  const [fixingSubmission, setFixingSubmission] = useState<FormSubmission | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all form templates
      const templates = await TemplateService.getFormTemplates(user?.organizationId || '');
      setAvailableTemplates(templates);

      // Load submissions with missing templateId
      const submissionsRef = collection(db, 'formSubmissions');
      const q = query(
        submissionsRef,
        where('organizationId', '==', user?.organizationId)
      );
      const snapshot = await getDocs(q);
      
      const broken: FormSubmission[] = [];
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.templateId || data.templateId === '') {
          broken.push({
            id: doc.id,
            templateId: data.templateId || '',
            templateName: data.templateName || 'Unknown',
            organizationId: data.organizationId,
            submittedBy: data.submittedBy,
            submittedByName: data.submittedByName,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            status: data.status || 'submitted',
            data: data.data || {}
          });
        }
      });

      setBrokenSubmissions(broken);
      // console.log(`🔧 Found ${broken.length} submissions with missing templateId`);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFixTemplateId = async () => {
    if (!fixingSubmission || !selectedTemplateId) return;

    try {
      const submissionRef = doc(db, 'formSubmissions', fixingSubmission.id);
      await updateDoc(submissionRef, {
        templateId: selectedTemplateId
      });
      
      toast.success('Template ID updated successfully!');
      setFixingSubmission(null);
      setSelectedTemplateId('');
      
      // Reload data
      loadData();
    } catch (error) {
      console.error('Error updating templateId:', error);
      toast.error('Failed to update template ID');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                🔧 Szakértő mód
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Hiányzó templateId javítása a beküldésekben
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : brokenSubmissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Minden rendben! ✨
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Nincs hiányzó templateId a beküldésekben.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {brokenSubmissions.length} beküldés hiányzó templateId-val
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    Ezek a beküldések nem jelennek meg helyesen a listákban és a customer portal-on.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {brokenSubmissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {submission.templateName || 'Unknown Form'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          ID: <span className="font-mono">{submission.id}</span>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Beküldve: {submission.createdAt.toLocaleDateString('hu-HU')} - {submission.submittedByName}
                        </p>
                      </div>
                      <button
                        onClick={() => setFixingSubmission(submission)}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
                      >
                        Javítás
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end space-x-3">
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Frissítés
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
            >
              Bezárás
            </button>
          </div>
        </div>
      </div>

      {/* Fix Modal */}
      {fixingSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Template kiválasztása
                </h3>
                <button
                  onClick={() => {
                    setFixingSubmission(null);
                    setSelectedTemplateId('');
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Submission: <span className="font-semibold">{fixingSubmission.templateName}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                    ID: <span className="font-mono">{fixingSubmission.id}</span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Válaszd ki a helyes űrlap sablont:
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Válassz sablont --</option>
                    {availableTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    ⚠️ Ez frissíti a templateId-t a Firestore-ban. Győződj meg róla, hogy a helyes sablont választod!
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setFixingSubmission(null);
                    setSelectedTemplateId('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Mégse
                </button>
                <button
                  onClick={handleFixTemplateId}
                  disabled={!selectedTemplateId}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Javítás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertModePanel;
